import React from "react";

// Deterministic placeholder widths keep skeletons from looking like a uniform
// block without introducing render-to-render churn (or non-reproducible tests).
const PLACEHOLDER_WIDTHS = ["72%", "46%", "61%", "38%", "55%", "43%", "66%", "51%"];
const PLACEHOLDER_HEIGHTS = [62, 38, 74, 47, 88, 55, 41, 70, 58, 34, 80, 49, 66, 43];

function placeholderWidth(row: number, column: number) {
  return PLACEHOLDER_WIDTHS[(row * 3 + column * 5) % PLACEHOLDER_WIDTHS.length];
}

function placeholderHeight(index: number) {
  return PLACEHOLDER_HEIGHTS[index % PLACEHOLDER_HEIGHTS.length];
}

/** Screen-reader-only announcement so a skeleton still says what is loading. */
export function SkeletonLabel({ children }: { children: string }) {
  return <span className="skeleton-label" role="status">{children}</span>;
}

export function SkeletonBar({
  width = "100%",
  height = 10,
  radius = 5,
  className = "",
}: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
}) {
  return <span
    className={`skeleton ${className}`.trim()}
    style={{ width, height, borderRadius: radius }}
    aria-hidden="true"
  />;
}

/** Count pill placeholder for section tabs and headings that render a total. */
export function SkeletonCount() {
  return <span className="skeleton skeleton-count" aria-hidden="true" />;
}

export function MetricSkeleton({ variant = "label" }: { variant?: "label" | "eyebrow" }) {
  return <article className="metric metric-skeleton" aria-hidden="true">
    {variant === "eyebrow"
      ? <>
        <p className="eyebrow"><SkeletonBar width={74} height={8} /></p>
        <strong><SkeletonBar width={92} height={26} /></strong>
        <SkeletonBar width={124} height={9} />
      </>
      : <>
        <SkeletonBar width={104} height={9} />
        <strong><SkeletonBar width={76} height={26} /></strong>
      </>}
    <div className="metric-line" />
  </article>;
}

export function MetricsSkeleton({
  label,
  count = 4,
  className = "",
  variant = "label",
}: {
  label: string;
  count?: number;
  className?: string;
  variant?: "label" | "eyebrow";
}) {
  return <section className={`metrics ${className}`.trim()} aria-busy="true">
    <SkeletonLabel>{label}</SkeletonLabel>
    {Array.from({ length: count }, (_, index) => <MetricSkeleton key={index} variant={variant} />)}
  </section>;
}

export function ChartSkeleton({ height = 240, columns = 14 }: { height?: number; columns?: number }) {
  return <div className="skeleton-chart" style={{ height }} aria-hidden="true">
    {Array.from({ length: columns }, (_, index) => <span
      className="skeleton"
      key={index}
      style={{ height: `${placeholderHeight(index)}%` }}
    />)}
  </div>;
}

export function DonutSkeleton({ height = 240 }: { height?: number }) {
  return <div className="skeleton-donut-wrap" style={{ height }} aria-hidden="true">
    <span className="skeleton skeleton-donut" />
  </div>;
}

/** Chart card body: heading meta line, plot area, and legend. */
export function ChartCardSkeleton({
  label,
  eyebrow,
  title,
  className = "",
  height = 240,
  shape = "bars",
  legend = 0,
}: {
  label: string;
  eyebrow: string;
  title: string;
  className?: string;
  height?: number;
  shape?: "bars" | "donut";
  legend?: number;
}) {
  return <article className={`activity-card chart-card ${className}`.trim()} aria-busy="true">
    <SkeletonLabel>{label}</SkeletonLabel>
    <div className="chart-heading">
      <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>
      <SkeletonBar width={58} height={9} />
    </div>
    {shape === "donut" ? <DonutSkeleton height={height} /> : <ChartSkeleton height={height} />}
    {legend > 0 && <div className="chart-legend">
      {Array.from({ length: legend }, (_, index) => <SkeletonBar key={index} width={64} height={8} />)}
    </div>}
  </article>;
}

/**
 * Table body placeholder. Renders `rows` skeleton rows plus one hidden row
 * carrying the announcement, so it drops straight into an existing `<tbody>`.
 */
export function SkeletonRows({
  label,
  columns,
  rows = 5,
}: {
  label: string;
  columns: number;
  rows?: number;
}) {
  return <>
    <tr className="skeleton-announcement">
      <td colSpan={columns}><SkeletonLabel>{label}</SkeletonLabel></td>
    </tr>
    {Array.from({ length: rows }, (_, row) => <tr className="skeleton-row" key={row} aria-hidden="true">
      {Array.from({ length: columns }, (_, column) => <td key={column}>
        <SkeletonBar width={placeholderWidth(row, column)} height={9} />
        {column === 0 && <SkeletonBar width="42%" height={8} />}
      </td>)}
    </tr>)}
  </>;
}

/** Stacked card placeholder for list panels, settings panels, and detail sections. */
export function SkeletonStack({
  label,
  rows = 3,
  lines = 2,
  className = "",
}: {
  label: string;
  rows?: number;
  lines?: number;
  className?: string;
}) {
  return <div className={`skeleton-stack ${className}`.trim()} aria-busy="true">
    <SkeletonLabel>{label}</SkeletonLabel>
    {Array.from({ length: rows }, (_, row) => <div key={row} aria-hidden="true">
      <SkeletonBar width={placeholderWidth(row, 0)} height={11} />
      {Array.from({ length: lines }, (_, line) => <SkeletonBar
        key={line}
        width={placeholderWidth(row, line + 1)}
        height={9}
      />)}
    </div>)}
  </div>;
}

export function SkeletonTimeline({ label, rows = 7 }: { label: string; rows?: number }) {
  return <div className="timeline global-timeline" aria-busy="true">
    <SkeletonLabel>{label}</SkeletonLabel>
    {Array.from({ length: rows }, (_, row) => <div key={row} aria-hidden="true">
      <i className="skeleton skeleton-dot" />
      <time><SkeletonBar width={52} height={9} /></time>
      <strong><SkeletonBar width={placeholderWidth(row, 1)} height={9} /></strong>
      <p><SkeletonBar width={placeholderWidth(row, 2)} height={9} /></p>
    </div>)}
  </div>;
}

/** Sidebar meter placeholder, tuned for the dark sidebar surface. */
export function SkeletonMeter() {
  return <div className="sidebar-meter skeleton-meter" aria-hidden="true">
    <div>
      <SkeletonBar width={38} height={8} />
      <SkeletonBar width={62} height={7} />
    </div>
    <div className="sidebar-meter-track"><i className="skeleton" /></div>
  </div>;
}

export function SkeletonMeters({ label, count = 3 }: { label: string; count?: number }) {
  return <>
    <SkeletonLabel>{label}</SkeletonLabel>
    {Array.from({ length: count }, (_, index) => <SkeletonMeter key={index} />)}
  </>;
}
